/**
 * Safe-redirect helper for the post-login bounce.
 *
 * Sign-in, sign-up, and the OAuth callback all honour a `?redirect_url=`
 * query parameter so the middleware can bring users back to the page they
 * tried to visit while signed-out. The raw value is attacker-controlled —
 * a crafted `?redirect_url=https://evil.com/phish` would otherwise hand
 * an already-authenticated session to a third-party origin on the very
 * next click.
 *
 * Rules: accept only same-origin **path** strings (must start with `/`,
 * must not start with `//` or `/\` which browsers parse as protocol-
 * relative). Anything else → fall back to /setup.
 */

const FALLBACK = '/setup';

const AUTH_PATHS = new Set([
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/auth/callback',
]);

export function safeRedirectPath(raw: string | null | undefined): string {
  if (!raw) return FALLBACK;
  if (!raw.startsWith('/')) return FALLBACK;
  // Protocol-relative (`//host`) and the Windows-style `/\host` variant
  // both navigate cross-origin. Reject both.
  if (raw.startsWith('//') || raw.startsWith('/\\')) return FALLBACK;
  // Don't loop the user back to an auth page after they just authed.
  const pathOnly = raw.split('?')[0]?.split('#')[0] ?? '';
  if (AUTH_PATHS.has(pathOnly)) return FALLBACK;
  return raw;
}
